import { supabase } from "@/lib/supabase";
import BaseService from "@/services/BaseService.ts";
import {CryptoWallet} from "@/integrations/supabase/crypto.models.ts";

/**
 * Service class for handling REIT-related operations, including fetching dividend payments.
 * This class provides methods to interact with the Supabase backend for REIT data.
 *
 * @class CryptoWalletService
 * @example
 * const reitService = new CryptoService();
 * const payments = await reitService.getReitDividendPayments(userId);
 */
export class CryptoWalletService extends BaseService {
  /**
   * Create holding for the user
   * @param {string} userId the user's ID
   * @param {CreateReitHoldingDto} request request to send
   */
  public async createWallet(
    userId: string,
    request: CryptoWalletCreateRequest,
  ): Promise<CryptoWallet> {
    try {
      const { data, error } = await supabase
          .from('crypto_wallets')
          .insert({ ...request, user_id: userId })
          .select()
          .single();
      if (error) throw error;
      return data as CryptoWallet;
    } catch (error) {
      console.error("Error creating Crypto wallet", error);
      await this.handleError(error);
    }
  }

  /**
   * Fetch all wallets for the user
   * @param {string} userId User ID
   */
  async fetchWallets(userId: string): Promise<CryptoWallet[]> {
    try {
      const { data, error } = await supabase
          .from('crypto_wallets')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error fetching Crypto wallets", error);
      await this.handleError(error);
    }
  }

  async deleteWallet(id: string): Promise<void> {
    try {
      const { error } = await supabase
          .from('crypto_wallets')
          .delete()
          .eq('id', id);
      if (error) throw error;
    } catch (error) {
      console.error("Error deleting Crypto wallet", error);
      await this.handleError(error);
    }
  }
}

export default new CryptoWalletService();
